import React, { memo, useMemo, useState, useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { createAction } from '@reduxjs/toolkit'
import { reduxBatch } from '@manaflair/redux-batch'
import { selectAll } from '../../../../store/slices/jobTally'
import { jobsActions, resultActions, uiActions } from '../../../../store/slices'

import queryString from 'query-string'

import Button from '../../../../../../components/Button'
import { JobTable, DuplicateModal, RemoveModal } from './components'

import {
  handleProductLinkClick,
} from '../../utils/utils'
import { useModal } from '../../../../../../utils/hooks'
import { RenderIf } from '../../../../../../utils/fns'

import { OUTPUT_TAB } from '../../../../common/constants'
import { displayFastenersAnchor, handleOrders } from '../../utils/fastener'
import { skipGAEventAction } from '../../../../../../utils/actions'

const JobTally = () => {
  const dispatch = useDispatch()

  const jobs = useSelector(selectAll)
  const [show, toggleModal] = useModal(null)
  const [currentSelection, setCurrentSelection] = useState({})

  const params = window.location.search ? queryString.parse(window.location.search) : {}

  const handleEdit = (value) => {
    const jobInputState = value.jobInputs

    reduxBatch(
      dispatch(skipGAEventAction(true)),
      Object.keys(jobInputState).map((key) => {
        const action = createAction(`${key}/updateCalculation`)

        return dispatch(action(jobInputState[key]))
      }),
      dispatch(jobsActions.setEditedJob({ uid: value.uid, id: value.id })),
      dispatch(uiActions.setActiveTab(OUTPUT_TAB.RESULT)),
      dispatch(resultActions.updateCalculation({ selectedRow: value }))
    )
  }

  const columns = useMemo(() => {
    const jobColumns = [
      {
        id: 'pre-actions',
        Header: () => null,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => {
          return (
            <div className="d-flex flex-column gap-y-2">
              <Button
                // eslint-disable-next-line react/prop-types
                onClick={() => handleEdit(row.original)}
              >
                Edit
              </Button>
              <Button
                onClick={() => {
                  // eslint-disable-next-line react/prop-types
                  setCurrentSelection(row.original)
                  toggleModal('duplicate')
                }}
              >
                Duplicate
              </Button>
            </div>
          )
        },
      },
      {
        Header: 'Job',
        accessor: 'uid',
      },
      {
        Header: 'Model',
        accessor: 'model',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value, row }) => {
          // eslint-disable-next-line react/prop-types
          const { series, hangerType, updatedWebStiffeners, updatedSpec } = row.original

          return (
            <div className="hs__firstCell">
              <div className="hs__modelInfo">
                <Button
                  className="hs__modelLink button"
                  variant="link"
                  onClick={() => handleProductLinkClick(value, series, hangerType)}
                >
                  <span className="button__text">
                    {value}
                    {updatedWebStiffeners}
                    <br />
                    {updatedSpec}
                  </span>
                </Button>
              </div>
            </div>
          )
        },
      },
      {
        Header: 'Quantity',
        accessor: 'quantity',
      },
      {
        Header: 'TF Fasteners',
        accessor: 'tfFastenersLabel',
        Cell: ({ value, row }) => {
          const { tfNailType } = row.original
          return displayFastenersAnchor(tfNailType, value)
        },
      },
      {
        Header: 'Face Fasteners',
        accessor: 'fFastenersLabel',
        Cell: ({ value, row }) => {
          const { hNailType } = row.original
          return displayFastenersAnchor(hNailType[0], value)
        },
      },
      {
        Header: 'Joist Fasteners',
        accessor: 'jFastenersLabel',
        Cell: ({ value, row }) => {
          const { jNailType } = row.original
          return displayFastenersAnchor(jNailType, value)
        },
      },
      {
        id: 'suf-actions',
        Header: () => null,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => {
          return (
            <a
              role="button"
              onClick={() => {
                // eslint-disable-next-line react/prop-types
                setCurrentSelection(row.original)
                toggleModal('remove')
              }}
            >
              <svg
                width="15"
                height="20"
                viewBox="0 0 15 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.07143 17.7778C1.07143 19 2.03571 20 3.21429 20H11.7857C12.9643 20 13.9286 19 13.9286 17.7778V4.44444H1.07143V17.7778ZM15 1.11111H11.25L10.1786 0H4.82143L3.75 1.11111H0V3.33333H15V1.11111Z"
                  fill="#A8671D"
                />
                <path d="M1.07143 17.7778C1.07143 19 2.03571 20 3.21429 20H11.7857C12.9643 20 13.9286 19 13.9286 17.7778V4.44444H1.07143V17.7778ZM15 1.11111H11.25L10.1786 0H4.82143L3.75 1.11111H0V3.33333H15V1.11111Z" />
              </svg>
            </a>
          )
        },
      },
    ]
    return jobColumns
  }, [])

  useEffect(() => {
    if (jobs.length === 0) dispatch(uiActions.setActiveTab(OUTPUT_TAB.RESULT))
  }, [jobs])

  const handleOrderJobList = () => {
    handleOrders(jobs)
    reduxBatch(
      jobs.map((j) => dispatch(resultActions.productOrderAction(j))),
    )
  }

  return (
    <div className="d-flex flex-column space-y-4 my-4">
      <div className="d-flex justify-between">
        {RenderIf(
          params?.source === 'EC',
          <Button className="button -xsmall" onClick={handleOrderJobList}>
            Order Job List
          </Button>,
        )}
      </div>
      <JobTable columns={columns} data={jobs} />

      {RenderIf(
        show === 'remove',
        <RemoveModal show={show === 'remove'} toggle={toggleModal} job={currentSelection} />,
      )}

      {RenderIf(
        show === 'duplicate',
        <DuplicateModal show={show === 'duplicate'} toggle={toggleModal} job={currentSelection} />,
      )}
    </div>
  )
}

export default memo(JobTally)
